/**
 * Template Registry
 *
 * Central configuration for all PDF export templates.
 * Templates are organized by ruleset and theme.
 */

export interface TemplateConfig {
  /** Unique identifier for the template (e.g., 'ignus-arcana') */
  id: string;
  /** Ruleset this template is designed for */
  rulesetId: string;
  /** Theme identifier (e.g., 'arcana', 'pragmata', 'dnd-official') */
  theme: string;
  /** Display name shown in template selector */
  displayName: string;
  /** Short description for the template selector */
  description: string;
  /** Path to the fillable PDF template (for fillable-pdf method) */
  templatePath: string;
  /** Which field mapping to use for this template */
  fieldMappingId: string;
  /** Whether this template supports Ignus-specific fields */
  supportsIgnusFields: boolean;
  /**
   * Method used to generate the PDF:
   * - 'fillable-pdf': Fill existing PDF form fields (WotC template)
   * - 'html-to-pdf': Render HTML template via server, then add form fields
   */
  renderMethod: 'fillable-pdf' | 'html-to-pdf';
  /** Template ID used by the server for HTML-to-PDF rendering */
  serverTemplateId?: string;
}

/**
 * Registry of all available PDF templates
 *
 * Templates per ruleset:
 * - Ignus: Classic, Compact
 * - SRD 5.1: D&D Official, Classic, Compact
 */
export const templateRegistry: Record<string, TemplateConfig> = {
  // Ignus Classic Template - uses HTML-to-PDF rendering
  'ignus-classic': {
    id: 'ignus-classic',
    rulesetId: 'ignus',
    theme: 'classic',
    displayName: 'Classic',
    description: 'Traditional tome aesthetic with Cinzel/Crimson fonts and faction styling (2-3 pages)',
    templatePath: '/mockups/ignus-template-classic.html',
    fieldMappingId: 'ignus-arcana',
    supportsIgnusFields: true,
    renderMethod: 'html-to-pdf',
    serverTemplateId: 'ignus-classic',
  },

  // Ignus Compact Template - uses HTML-to-PDF rendering
  'ignus-compact': {
    id: 'ignus-compact',
    rulesetId: 'ignus',
    theme: 'compact',
    displayName: 'Compact',
    description: 'Dense layout optimized for minimal ink usage with Ignus styling (1-2 pages)',
    templatePath: '/mockups/ignus-template-compact.html',
    fieldMappingId: 'ignus-arcana',
    supportsIgnusFields: true,
    renderMethod: 'html-to-pdf',
    serverTemplateId: 'ignus-compact',
  },

  // SRD 5.1 Template - uses fillable PDF approach
  'srd-dnd-official': {
    id: 'srd-dnd-official',
    rulesetId: 'srd-5.1',
    theme: 'dnd-official',
    displayName: 'D&D Official',
    description: 'Standard Wizards of the Coast character sheet layout (fillable)',
    templatePath: '/templates/character-sheet.pdf',
    fieldMappingId: 'wotc',
    supportsIgnusFields: false,
    renderMethod: 'fillable-pdf',
  },

  // SRD 5.1 Classic Template - uses HTML-to-PDF rendering
  'srd-classic': {
    id: 'srd-classic',
    rulesetId: 'srd-5.1',
    theme: 'classic',
    displayName: 'Classic',
    description: 'Traditional tome aesthetic with serif fonts and decorative flourishes (2-3 pages)',
    templatePath: '/mockups/srd-template-classic.html',
    fieldMappingId: 'srd-html',
    supportsIgnusFields: false,
    renderMethod: 'html-to-pdf',
    serverTemplateId: 'srd-classic',
  },

  // SRD 5.1 Compact Template - uses HTML-to-PDF rendering
  'srd-compact': {
    id: 'srd-compact',
    rulesetId: 'srd-5.1',
    theme: 'compact',
    displayName: 'Compact',
    description: 'Dense layout optimized for minimal ink usage and game-ready reference (1-2 pages)',
    templatePath: '/mockups/srd-template-compact.html',
    fieldMappingId: 'srd-html',
    supportsIgnusFields: false,
    renderMethod: 'html-to-pdf',
    serverTemplateId: 'srd-compact',
  },
};

/**
 * FUTURE TEMPLATES (keep for later implementation)
 *
 * Pragmata theme mockups are available at:
 * - /apps/web/public/mockups/ignus-template-c.html
 *
 * To add these templates:
 * 1. Generate fillable PDF from HTML mockup
 * 2. Add form fields using scripts/add-fields-from-html.mjs
 * 3. Create field mapping in fieldMappings/
 * 4. Uncomment config below
 *
 * 'ignus-pragmata': {
 *   id: 'ignus-pragmata',
 *   rulesetId: 'ignus',
 *   theme: 'pragmata',
 *   displayName: 'Pragmata',
 *   description: 'Modern minimal design with clean grid-based layout',
 *   templatePath: '/templates/ignus-pragmata.pdf',
 *   fieldMappingId: 'ignus-pragmata',
 *   supportsIgnusFields: true,
 * },
 * 'srd-arcana': { ... },
 * 'srd-pragmata': { ... },
 */

/**
 * Get all templates available for a specific ruleset
 */
export function getTemplatesForRuleset(rulesetId: string): TemplateConfig[] {
  return Object.values(templateRegistry).filter(
    (template) => template.rulesetId === rulesetId
  );
}

/**
 * Get the default template for a ruleset
 * - Ignus: Classic theme
 * - SRD 5.1: Classic theme
 */
export function getDefaultTemplateForRuleset(rulesetId: string): TemplateConfig | undefined {
  const templates = getTemplatesForRuleset(rulesetId);
  if (templates.length === 0) return undefined;

  // Default preferences by ruleset - prefer 'classic' theme for both
  if (rulesetId === 'ignus') {
    return templates.find((t) => t.theme === 'classic') ?? templates[0];
  }
  if (rulesetId === 'srd-5.1') {
    return templates.find((t) => t.theme === 'classic') ?? templates[0];
  }

  return templates[0];
}

/**
 * Get a specific template by ID
 */
export function getTemplate(templateId: string): TemplateConfig | undefined {
  return templateRegistry[templateId];
}

/**
 * Get all unique themes across all templates
 */
export function getAllThemes(): string[] {
  const themes = new Set(Object.values(templateRegistry).map((t) => t.theme));
  return Array.from(themes);
}
